import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Settings2, Image } from "lucide-react";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";
import { useState, useMemo } from "react";

interface Workspace {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  product_users: { count: number }[];
}

interface WorkspacesTableProps {
  products: Workspace[];
}

export const WorkspacesTable = ({ products }: WorkspacesTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination({
    initialPage: 1,
    initialPageSize: 10
  });

  const paginatedProducts = paginateData(products);
  
  // Um único timestamp para a sessão atual que garante que as imagens 
  // não sejam recarregadas a cada renderização
  const sessionTimestamp = useMemo(() => Date.now(), []);
  
  // Mapa de timestamps específicos por produto, atualizado apenas quando um produto é realmente modificado
  const [productTimestamps] = useState<Record<string, number>>({});
  
  // Gera um timestamp para um produto específico
  const getProductTimestamp = (workspaceId: string) => {
    return productTimestamps[workspaceId] || sessionTimestamp;
  };
  

  // Usamos a versão dos logos do WorkspaceContext para cache-busting das imagens

  const getInitials = (text: string) => {
    return text.split(' ').map(word => word[0]?.toUpperCase()).join('').slice(0, 2) || '?';
  };


  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("settings.workspaces.fields.logo")}</TableHead>
            <TableHead>{t("settings.workspaces.fields.name")}</TableHead>
            <TableHead>{t("settings.workspaces.fields.users")}</TableHead>
            <TableHead className="w-[100px]">{t("common.labels.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProducts.map((workspace) => (
            <TableRow key={workspace.id}>
              <TableCell>
                <Avatar className="h-10 w-10">
                  <AvatarImage 
                    src={workspace.logo_url ? `${workspace.logo_url}?t=${getProductTimestamp(workspace.id)}` : ''} 
                    alt={workspace.name} 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target) target.src = '';
                    }}
                  />
                  <AvatarFallback className="bg-muted">
                    {workspace.logo_url ? (
                      <Image className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      getInitials(workspace.name)
                    )}
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell>{workspace.name}</TableCell>
              <TableCell>
                <Badge variant="secondary">
                  {workspace.product_users[0]?.count || 0} {t("settings.workspaces.users")}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/products/${workspace.id}/config`)}
                    title={t("common.actions.configure", "Configurar")}
                  >
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <DataPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={products.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[5, 10, 20, 50]}
      />

    </>
  );
};
